import { type FC } from "react";
import { shallowEqual, useSelector } from "react-redux";

import { renderSampleParentAttributesWidgets } from "@/app-utils/attributes-widgets.tsx";
import { useEventNotificationGridUpdate } from "@/app-utils/hooks/useNotificationUpdate.ts";
import type { InputTableQuerySpec, Parent } from "@/graphql.ts";
import {
  createUniqueKeyFromTableSpec,
  getTableDataSlice,
  type ITableData,
  type ITableDataState,
} from "@/redux/modules/table-data.ts";
import type { IAppState } from "@/redux/reducer.ts";
import { DATATABLE_ID_TYPE, ENTITY_TYPE, EVENT_TABLE_COLUMNS } from "@/settings/settings";
import DataTableContainer from "@/ui-modules/core/grid/DataTableContainer.tsx";
import { AttributesListWidget } from "@/ui-modules/core/navigation-panel/AttributeWidget.tsx";

import styleLayout from "../../core/styles/PageLayout.module.scss";
import GenericEntitySummaryCardView from "../GenericEntitySummaryView.js";

interface SampleParentEventsViewProps {
  parentId: number;
  parent: Parent;
}

/**
 * Either sampleId or parentId, both at the same time is prohibited
 */
export const SampleParentEventsView: FC<SampleParentEventsViewProps> = (props) => {
  const eventsTableSpec: InputTableQuerySpec = {
    resultType: ENTITY_TYPE.EVENT,
    id: props.parentId,
    idType: DATATABLE_ID_TYPE.ENTITY,
    queryType: ENTITY_TYPE.SAMPLE_PARENT,
  };

  const tableData: ITableData = useSelector<IAppState, ITableData>(
    (state) => getTableDataSlice(state.tableData, eventsTableSpec),
    shallowEqual,
  );

  const eventsTableInitOptions = {
    sortExpression: `-${EVENT_TABLE_COLUMNS.CREATED}`,
  };

  // create count label
  const getCount = () => {
    if (tableData?.totalRowCount) {
      if (tableData.filteredRowCount === tableData.totalRowCount) {
        return String(tableData.totalRowCount);
      } else if (tableData.filteredRowCount < tableData.totalRowCount) {
        return `${tableData.filteredRowCount} of ${tableData.totalRowCount}`;
      }
    }
    return undefined;
  };

  useEventNotificationGridUpdate(eventsTableSpec);

  return (
    <>
      <GenericEntitySummaryCardView
        title={props.parent?.name ?? ""}
        subtitle={props.parent?.description ?? "Playbook Events"}
        entity={{ id: props.parentId, groupType: ENTITY_TYPE.SAMPLE_PARENT }}
        project={props.parent?.project}
        setDocumentTitle
        badge={getCount()}
        tableQuerySpec={eventsTableSpec}
        tableDataState={tableData as unknown as ITableDataState}
        headerAttributes={[
          <AttributesListWidget
            attributes={renderSampleParentAttributesWidgets(props.parent)}
            key={"gene-expression-view-widget-1"}
          />,
        ]}
      />
      <div className={styleLayout.page} style={{ backgroundColor: "white" }}>
        <DataTableContainer
          key={createUniqueKeyFromTableSpec(eventsTableSpec)}
          tableSpec={eventsTableSpec}
          hideCheckboxes={true}
          tableInitializationOptions={eventsTableInitOptions}
          tableData={tableData}
          doNotShowPerfGuidance={false}
          wrapColumnHeaders={false}
          sequenceFeatureTypes={{
            aaFeatureTypes: null,
            markerTypes: null,
            nuFeatureTypes: null,
          }}
        />
      </div>
    </>
  );
};
